import React, { useEffect } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, DialogContent, Grid, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../../modules/localization/translations"
import { StandardDialog } from "../StandardDialog"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import {
    ConfigQuery,
    fetchServiceCenterResourceDetails,
    ServiceCenterResourceDetailsResponse,
} from "../../createOrder/createOrderApi"
import { ServiceCenterResourceDetails } from "../../createOrder/createOrderTypes"

import { ResourceDetailsDialogSkeleton } from "./ResourceDetailsDialogSkeleton"

type StyleProps = Readonly<{
    isImage?: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isImage = false }) => ({
    dialogPaper: {
        maxWidth: "50rem",
    },
    dialogContent: {
        padding: "2rem 4rem 4rem",
        display: "flex",
        flexWrap: "nowrap",
        flexDirection: "row",
        gap: "1rem",
        color: theme.palette.tailwind.slate[900],
    },
    list: {
        display: "flex",
        flexDirection: isImage ? "column" : "row",
        gap: "2rem",
        flexWrap: "wrap",
        "& > div": {
            width: isImage ? "100%" : "46%",
        },
    },
    details: {
        color: theme.palette.tailwind.slate[700],
    },
    detailsListWrap: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        flexWrap: "wrap",
    },
    imageWrap: {
        width: "100%",
    },
    image: {
        width: "100%",
        height: "auto",
        objectFit: "contain",
    },
    dot: {
        flex: "none",
        display: "block",
        width: ".5rem",
        height: ".5rem",
        borderRadius: "50%",
        backgroundColor: theme.palette.tailwind.slate[500],
    },
}))

type ListItem = Readonly<{
    name: string
}>

type ArrangementListProps = Readonly<{
    items: ListItem[]
}>

export function ArrangementList({ items }: ArrangementListProps) {
    const { classes } = useStyles({})

    return (
        <Box className={classes.detailsListWrap}>
            {items.map((item, index) => (
                <React.Fragment key={item.name}>
                    <Typography variant="body1" className={classes.details}>
                        {item.name}
                    </Typography>
                    {index < items.length - 1 && <span className={classes.dot} />}
                </React.Fragment>
            ))}
        </Box>
    )
}

type DetailItemProps = Readonly<{
    labelId: TranslationKey
    labelFallback: string
    value?: React.ReactNode
    children?: React.ReactNode
}>

function DetailItem({ labelId, labelFallback, value, children }: DetailItemProps) {
    const { classes } = useStyles({})

    return (
        <Box>
            <LocalizedStrict id={labelId}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {labelFallback}
                </Typography>
            </LocalizedStrict>
            {value !== undefined ? (
                <Typography variant="body1" className={classes.details}>
                    {value}
                </Typography>
            ) : (
                children
            )}
        </Box>
    )
}

const transformData = (data: ServiceCenterResourceDetailsResponse): ServiceCenterResourceDetails | undefined =>
    data.resources?.[0]

type ResourceDetailsDialogProps = Readonly<{
    id: string
    handleClose: () => void
}>

export function ResourceDetailsDialog({ id, handleClose }: ResourceDetailsDialogProps) {
    const { data, isInitialLoading, isError } = useApiQuery(fetchServiceCenterResourceDetails, {
        queryName: ConfigQuery.RESOURCE_DETAILS,
        dependencies: { id },
        enabled: !!id,
        select: transformData,
    })

    useEffect(() => {
        if (isError) handleClose()
    }, [isError, handleClose])

    const { classes } = useStyles({ isImage: !!data?.imageUrl })

    return (
        <StandardDialog
            open={true}
            onClose={handleClose}
            dialogTitle={data?.name}
            slotProps={{
                paper: { className: classes.dialogPaper },
            }}
            maxWidthProp="md"
        >
            <DialogContent className={classes.dialogContent}>
                {isInitialLoading ? (
                    <ResourceDetailsDialogSkeleton />
                ) : (
                    <>
                        <Grid item xs={data?.imageUrl ? 6 : 12} className={classes.list}>
                            {data?.description && (
                                <DetailItem
                                    labelId="resource-details-dialog-description"
                                    labelFallback="Description"
                                    value={data.description}
                                />
                            )}
                            {data?.serviceCenterName && (
                                <DetailItem
                                    labelId="resource-details-dialog-service-center"
                                    labelFallback="Service center"
                                    value={data.serviceCenterName}
                                />
                            )}
                            {Number.isFinite(data?.room?.floor) && (
                                <DetailItem
                                    labelId="resource-details-dialog-floor"
                                    labelFallback="Floor"
                                    value={data?.room.floor}
                                />
                            )}
                            {Number.isFinite(data?.room?.squareMeters) && (
                                <DetailItem
                                    labelId="resource-details-dialog-square-meters"
                                    labelFallback="Square meters"
                                    value={data?.room.squareMeters}
                                />
                            )}
                            {data?.room?.walkDirection && (
                                <DetailItem
                                    labelId="resource-details-dialog-walk-direction"
                                    labelFallback="Walk direction"
                                    value={data.room.walkDirection}
                                />
                            )}
                            {Number.isFinite(data?.room?.seats) && (
                                <DetailItem labelId="resource-details-dialog-capacity" labelFallback="Capacity">
                                    <LocalizedStrict
                                        id="resource-details-dialog-capacity-quantity"
                                        vars={{ quantity: data?.room.seats ?? 0 }}
                                    >
                                        <Typography variant="body1" className={classes.details}>
                                            {data?.room.seats}
                                        </Typography>
                                    </LocalizedStrict>
                                </DetailItem>
                            )}
                            {!!data?.room?.tableSetups?.length && (
                                <DetailItem
                                    labelId="resource-details-dialog-table-arrangement"
                                    labelFallback="Table arrangement"
                                >
                                    <ArrangementList items={data.room.tableSetups} />
                                </DetailItem>
                            )}
                        </Grid>
                        {data?.imageUrl && (
                            <Grid item xs={6} className={classes.imageWrap}>
                                <img src={data.imageUrl} className={classes.image} />
                            </Grid>
                        )}
                    </>
                )}
            </DialogContent>
        </StandardDialog>
    )
}
